/* Shared report-image rendering engine.
   Produces fixed 1920×1080 PNG data URLs for the Questions / Answers / Winners
   reports. Used by both the admin/reports download buttons AND the headless
   render page (/report-render) that the backend drives to email the EXACT same
   images. Keep this the single source of truth for the canvas designs. */

export const OUT_W = 1920, OUT_H = 1080;
const F = "'Work Sans', Arial, sans-serif";
export const PER_PAGE = 3;

export const GAME_META = {
  rebus:  { label: "REBUS",         color: "#FF3B30" },
  word:   { label: "WORD BUILDING", color: "#1E88E5" },
  sudoku: { label: "SUDOKU",        color: "#16a34a" },
  trivia: { label: "TRIVIA",        color: "#9333EA" },
};

export const loadImg = (src) => new Promise((res) => {
  if (!src) return res(null);
  const im = new Image();
  im.onload = () => res(im);
  im.onerror = () => res(null);
  im.src = src;
});

function _mkCanvas(W, H) {
  const canvas = document.createElement("canvas");
  canvas.width = OUT_W; canvas.height = OUT_H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FDFBF7"; ctx.fillRect(0, 0, OUT_W, OUT_H);
  const s = Math.min(OUT_W / W, OUT_H / H);
  ctx.translate((OUT_W - W * s) / 2, (OUT_H - H * s) / 2);
  ctx.scale(s, s);
  return { canvas, ctx };
}

function _wrapText(ctx, text, x, y, maxW, lineH) {
  const words = String(text || "").split(" ");
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, y); y += lineH; line = w;
    } else { line = test; }
  }
  if (line) { ctx.fillText(line, x, y); y += lineH; }
  return y;
}

function _fitFont(ctx, text, weight, start, maxW, min) {
  let s = start; ctx.font = `${weight} ${s}px ${F}`;
  while (s > min && ctx.measureText(text).width > maxW) { s -= 1; ctx.font = `${weight} ${s}px ${F}`; }
  return s;
}

function _drawSudokuGrid(ctx, sud, gx, gy, size, showSolution = true) {
  const puz = sud.puzzle || [];
  const N = puz.length || 9;
  const box = N === 6 ? { r: 2, c: 3 } : N === 4 ? { r: 2, c: 2 } : { r: 3, c: 3 };
  const cell = size / N;
  ctx.fillStyle = "#ffffff"; ctx.fillRect(gx, gy, size, size);
  const sol = sud.solution || [];
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      const puzV = ((puz[r] || [])[c]) || 0;
      const v = showSolution ? ((sol[r] || [])[c]) : puzV;
      if (!v) continue;
      const clue = puzV !== 0;
      ctx.fillStyle = clue ? "#0A0A0A" : "#1E88E5";
      ctx.font = `${clue ? "800" : "600"} ${Math.round(cell * 0.5)}px 'Work Sans', Arial, sans-serif`;
      ctx.fillText(String(v), gx + c * cell + cell / 2, gy + r * cell + cell / 2 + 1);
    }
  }
  ctx.textBaseline = "alphabetic";
  for (let i = 0; i <= N; i++) {
    const vThick = i % box.c === 0;
    ctx.lineWidth = vThick ? 3 : 1;
    ctx.strokeStyle = vThick ? "#0A0A0A" : "#c9c9c9";
    ctx.beginPath(); ctx.moveTo(gx + i * cell, gy); ctx.lineTo(gx + i * cell, gy + size); ctx.stroke();
    const hThick = i % box.r === 0;
    ctx.lineWidth = hThick ? 3 : 1;
    ctx.strokeStyle = hThick ? "#0A0A0A" : "#c9c9c9";
    ctx.beginPath(); ctx.moveTo(gx, gy + i * cell); ctx.lineTo(gx + size, gy + i * cell); ctx.stroke();
  }
}

function _drawHeader(ctx, W, headerH, badge, dateStr, logo, pageLabel, subtitle) {
  ctx.fillStyle = "#0A0A0A"; ctx.fillRect(0, 0, W, headerH);
  const lsz = 62, lx = 28, ly = 18;
  if (logo) {
    ctx.save();
    ctx.beginPath(); ctx.arc(lx + lsz / 2, ly + lsz / 2, lsz / 2, 0, Math.PI * 2); ctx.closePath();
    ctx.fillStyle = "#ffffff"; ctx.fill(); ctx.clip();
    ctx.drawImage(logo, lx, ly, lsz, lsz);
    ctx.restore();
    ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(lx + lsz / 2, ly + lsz / 2, lsz / 2, 0, Math.PI * 2); ctx.stroke();
  }
  ctx.textAlign = "left";
  ctx.fillStyle = "#39FF14"; ctx.font = `900 24px ${F}`;
  ctx.fillText(badge, lx - 2, ly + lsz + 30);
  ctx.textAlign = "center";
  ctx.fillStyle = "#ffffff"; ctx.font = `800 30px ${F}`;
  ctx.fillText("TekWissen Employee Fun Zone", W / 2, headerH / 2 + (subtitle ? 2 : 10));
  if (subtitle) {
    ctx.fillStyle = "#FFD600"; ctx.font = `700 17px ${F}`;
    ctx.fillText(subtitle, W / 2, headerH / 2 + 30);
  }
  ctx.textAlign = "right";
  ctx.fillStyle = "#FFD600"; ctx.font = `600 16px ${F}`;
  ctx.fillText(dateStr, W - 24 - 4, headerH - 16);
  if (pageLabel) {
    ctx.fillStyle = "#9ca3af"; ctx.font = `700 13px ${F}`;
    ctx.fillText(pageLabel, W - 24 - 4, ly + lsz + 30);
  }
  ctx.textAlign = "left";
}

function _drawQACard(ctx, card, x, colTop, colW, colBodyH, colHeaderH, mode) {
  ctx.strokeStyle = "#0A0A0A"; ctx.lineWidth = 3;
  ctx.strokeRect(x, colTop, colW, colBodyH);
  ctx.fillStyle = card.color; ctx.fillRect(x, colTop, colW, colHeaderH);
  ctx.fillStyle = "#ffffff"; ctx.font = `900 22px ${F}`; ctx.textAlign = "left";
  ctx.fillText(card.label, x + 14, colTop + 31);

  const p = 16;
  const by = colTop + colHeaderH, bh = colBodyH - colHeaderH;
  const cx = x + p, cy = by + p, cw = colW - 2 * p;
  const empty = (msg) => {
    ctx.fillStyle = "#9ca3af"; ctx.font = `500 16px ${F}`; ctx.textAlign = "center";
    ctx.fillText(msg, x + colW / 2, by + bh / 2); ctx.textAlign = "left";
  };

  if (card.type === "rebus") {
    const rebus = card.rebus || [];
    if (rebus.length === 0) { empty("No puzzle today"); return; }
    const img = card._img;
    if (mode === "questions") {
      const hint = rebus[0].hint || "";
      const boxH = bh - (hint ? 72 : 34);
      if (img && img.width) {
        const r = Math.min(cw / img.width, boxH / img.height);
        const dw = img.width * r, dh = img.height * r;
        ctx.drawImage(img, cx + (cw - dw) / 2, cy + (boxH - dh) / 2, dw, dh);
      } else {
        ctx.fillStyle = "#f3f4f6"; ctx.fillRect(cx, cy, cw, boxH);
        ctx.fillStyle = "#9ca3af"; ctx.font = `500 13px ${F}`; ctx.textAlign = "center";
        ctx.fillText("(no image)", x + colW / 2, cy + boxH / 2); ctx.textAlign = "left";
      }
      ctx.textAlign = "center";
      if (hint) {
        ctx.fillStyle = "#737373"; ctx.font = `700 13px ${F}`;
        ctx.fillText("HINT", x + colW / 2, by + bh - 46);
        ctx.fillStyle = "#0A0A0A"; ctx.font = `600 15px ${F}`;
        _wrapText(ctx, hint, x + colW / 2, by + bh - 24, cw, 18);
      } else {
        ctx.fillStyle = "#737373"; ctx.font = `600 15px ${F}`;
        ctx.fillText("Guess the phrase!", x + colW / 2, by + bh - 16);
      }
      ctx.textAlign = "left";
    } else {
      const boxW = cw, boxH = bh - 60;
      if (img && img.width) {
        const r = Math.min(boxW / img.width, boxH / img.height);
        const dw = img.width * r, dh = img.height * r;
        ctx.drawImage(img, cx + (boxW - dw) / 2, cy + (boxH - dh) / 2, dw, dh);
      } else {
        ctx.fillStyle = "#f3f4f6"; ctx.fillRect(cx, cy, boxW, boxH);
        ctx.fillStyle = "#9ca3af"; ctx.font = `500 13px ${F}`; ctx.textAlign = "center";
        ctx.fillText("(no image)", x + colW / 2, cy + boxH / 2); ctx.textAlign = "left";
      }
      const ans = (rebus[0].answer || "").toUpperCase();
      _fitFont(ctx, ans, 900, 26, cw, 12);
      ctx.fillStyle = "#0A0A0A"; ctx.textAlign = "center";
      ctx.fillText(ans, x + colW / 2, by + bh - 18);
      ctx.textAlign = "left";
    }
  } else if (card.type === "word") {
    const word = card.word;
    if (!word) { empty("No word today"); return; }
    if (mode === "questions") {
      ctx.textAlign = "center";
      let ty = cy + 66;
      ctx.fillStyle = "#737373"; ctx.font = `700 14px ${F}`;
      ctx.fillText("UNSCRAMBLE THESE LETTERS", x + colW / 2, ty); ty += 66;
      const ltr = (word.letters || "").toUpperCase().split("").join(" ");
      _fitFont(ctx, ltr, 900, 46, cw, 16);
      ctx.fillStyle = "#1E88E5"; ctx.fillText(ltr, x + colW / 2, ty); ty += 58;
      const n = (word.letters || "").replace(/[^a-z]/gi, "").length;
      ctx.fillStyle = "#0A0A0A"; ctx.font = `700 16px ${F}`;
      ctx.fillText(`${n} letters`, x + colW / 2, ty); ty += 40;
      ctx.fillStyle = "#737373"; ctx.font = `500 14px ${F}`;
      _wrapText(ctx, "Rearrange the letters to find today's word.", x + colW / 2, ty, cw, 18);
      ctx.textAlign = "left";
    } else {
      let ty = cy + 8;
      ctx.fillStyle = "#737373"; ctx.font = `700 13px ${F}`;
      ctx.fillText("SCRAMBLED LETTERS", cx, ty); ty += 30;
      const ltr = (word.letters || "").toUpperCase().split("").join(" ");
      _fitFont(ctx, ltr, 800, 22, cw, 10);
      ctx.fillStyle = "#0A0A0A"; ctx.fillText(ltr, cx, ty); ty += 34;
      ctx.fillStyle = "#737373"; ctx.font = `700 13px ${F}`;
      ctx.fillText("ANSWER", cx, ty); ty += 8;
      const aw = (word.word || "").toUpperCase();
      const af = _fitFont(ctx, aw, 900, 32, cw, 14);
      ctx.fillStyle = "#1E88E5"; ctx.fillText(aw, cx, ty + af * 0.82); ty += af + 16;
      if (word.meaning) {
        ctx.fillStyle = "#737373"; ctx.font = `700 13px ${F}`;
        ctx.fillText("MEANING", cx, ty); ty += 22;
        ctx.fillStyle = "#0A0A0A"; ctx.font = `500 15px ${F}`;
        _wrapText(ctx, word.meaning, cx, ty, cw, 20);
      }
    }
  } else if (card.type === "sudoku") {
    const sud = card.sudoku;
    if (!sud) { empty("No puzzle today"); return; }
    const G = Math.min(cw, bh - 44);
    const gx = cx + (cw - G) / 2, gy = cy;
    _drawSudokuGrid(ctx, sud, gx, gy, G, mode === "answers");
    ctx.fillStyle = "#0A0A0A"; ctx.font = `800 15px ${F}`; ctx.textAlign = "center";
    ctx.fillText(`Difficulty: ${(sud.difficulty || "medium").toUpperCase()}`, x + colW / 2, gy + G + 26);
    ctx.textAlign = "left";
  } else {
    const g = card.data || {};
    const img = card._img;
    let ty = cy + 8;
    if (img && img.width) {
      const boxH = Math.min(bh - 120, 180);
      const r = Math.min(cw / img.width, boxH / img.height);
      const dw = img.width * r, dh = img.height * r;
      ctx.drawImage(img, cx + (cw - dw) / 2, ty, dw, dh);
      ty += dh + 18;
    }
    const q = g.question || g.prompt || g.title || "";
    if (q) {
      ctx.fillStyle = "#737373"; ctx.font = `700 13px ${F}`; ctx.textAlign = "left";
      ctx.fillText(mode === "questions" ? "QUESTION" : "PROMPT", cx, ty); ty += 22;
      ctx.fillStyle = "#0A0A0A"; ctx.font = `600 17px ${F}`;
      ty = _wrapText(ctx, q, cx, ty, cw, 22) + 12;
    }
    if (mode === "questions") {
      const opts = g.options || [];
      if (opts.length) {
        ctx.fillStyle = "#0A0A0A"; ctx.font = `500 15px ${F}`;
        opts.forEach((o, i) => { ty = _wrapText(ctx, `${String.fromCharCode(65 + i)}. ${o}`, cx, ty, cw, 20); });
      } else if (!q) { empty("Scheduled"); }
    } else {
      const ans = g.answer != null ? String(g.answer) : "";
      if (ans) {
        ctx.fillStyle = "#737373"; ctx.font = `700 13px ${F}`;
        ctx.fillText("ANSWER", cx, ty); ty += 24;
        const af = _fitFont(ctx, ans.toUpperCase(), 900, 26, cw, 14);
        ctx.fillStyle = card.color; ctx.fillText(ans.toUpperCase(), cx, ty + af * 0.4); ty += af + 14;
      }
      if (g.meaning || g.explanation) {
        ctx.fillStyle = "#737373"; ctx.font = `700 13px ${F}`;
        ctx.fillText(g.meaning ? "MEANING" : "WHY", cx, ty); ty += 22;
        ctx.fillStyle = "#0A0A0A"; ctx.font = `500 15px ${F}`;
        _wrapText(ctx, g.meaning || g.explanation, cx, ty, cw, 20);
      }
      if (!ans && !g.meaning && !g.explanation && !q) empty("No answer");
    }
  }
}

function _buildQACards(data, assets) {
  const cards = [];
  if ((data.rebus || []).length) cards.push({ type: "rebus", ...GAME_META.rebus, rebus: data.rebus, _img: (assets.rebusImgs || [])[0] });
  if (data.word) cards.push({ type: "word", ...GAME_META.word, word: data.word });
  if (data.sudoku) cards.push({ type: "sudoku", ...GAME_META.sudoku, sudoku: data.sudoku });
  (data.extra || []).forEach((g, i) => {
    const meta = GAME_META[g.key] || { label: (g.label || g.key || "GAME").toUpperCase(), color: g.color || "#0A0A0A" };
    cards.push({ type: g.type && GAME_META[g.type] ? g.type : "generic", ...meta, data: g, _img: (assets.extraImgs || [])[i], ...(g.color ? { color: g.color } : {}) });
  });
  return cards;
}

function _buildWinnerCards(data) {
  const games = data.games || {};
  const units = data.units || {};
  const order = ["rebus", "word", "sudoku"];
  const keys = [...order.filter((k) => k in games), ...Object.keys(games).filter((k) => !order.includes(k))];
  return keys.map((k) => {
    const meta = GAME_META[k] || { label: k.toUpperCase(), color: "#0A0A0A" };
    const unit = (units[k] || "pts").toUpperCase();
    return { key: k, ...meta, rows: games[k] || [], unit };
  });
}

function _paginate(arr) {
  const pages = [];
  for (let i = 0; i < arr.length; i += PER_PAGE) pages.push(arr.slice(i, i + PER_PAGE));
  return pages;
}

function _fmtDay(d) {
  return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

// Returns [{ filename, dataUrl }]. mode = "questions" | "answers".
export function generateReportImages(mode, data, assets = {}) {
  const cards = _buildQACards(data, assets);
  if (cards.length === 0) return [];
  const padX = 24, colGap = 20, colW = 360;
  const headerH = 150, colTop = headerH + 24, colHeaderH = 46, colBodyH = 430;
  const H = colTop + colBodyH + 46;
  const badge = mode === "questions" ? "DAILY QUESTIONS" : "DAILY ANSWERS";
  const dstr = _fmtDay(data.date);
  const pages = _paginate(cards);
  const subtitle = mode === "questions" ? "Take a few minutes to play today's games — good luck!" : "";
  const out = [];
  pages.forEach((pageCards, pi) => {
    const n = pageCards.length;
    const W = padX * 2 + colW * n + colGap * Math.max(0, n - 1);
    const { canvas, ctx } = _mkCanvas(W, H);
    const pageLabel = pages.length > 1 ? `PAGE ${pi + 1} / ${pages.length}` : "";
    _drawHeader(ctx, W, headerH, badge, dstr, assets.logo, pageLabel, subtitle);
    pageCards.forEach((card, ci) => {
      const x = padX + ci * (colW + colGap);
      _drawQACard(ctx, card, x, colTop, colW, colBodyH, colHeaderH, mode);
    });
    ctx.fillStyle = "#9ca3af"; ctx.font = `500 13px ${F}`; ctx.textAlign = "left";
    ctx.fillText("Generated " + new Date().toLocaleString(), padX + 4, H - 18);
    const suffix = pages.length > 1 ? `_page${pi + 1}` : "";
    out.push({ filename: `funzone_${mode}_${data.date}${suffix}.png`, dataUrl: canvas.toDataURL("image/png") });
  });
  return out;
}

// Returns [{ filename, dataUrl }]. Supports single day OR a date range (start/end).
export function generateWinnerImages(data, logo) {
  const cards = _buildWinnerCards(data);
  if (cards.length === 0) return [];
  const padX = 24, colGap = 20, colW = 360;
  const headerH = 150, colTop = headerH + 24, colHeaderH = 46, rowH = 54, maxRows = 6;
  const colBodyH = colHeaderH + maxRows * rowH + 14;
  const H = colTop + colBodyH + 46;
  const isRange = data.start && data.end && data.start !== data.end;
  const badge = isRange ? "TOP PERFORMERS" : "TOP 6 WINNERS";
  const dstr = isRange ? `${_fmtDay(data.start)}  –  ${_fmtDay(data.end)}` : _fmtDay(data.date);
  const fileBase = isRange ? `top_performers_${data.start}_to_${data.end}` : `top6_winners_${data.date}`;
  const pages = _paginate(cards);
  const out = [];
  pages.forEach((pageCards, pi) => {
    const n = pageCards.length;
    const W = padX * 2 + colW * n + colGap * Math.max(0, n - 1);
    const { canvas, ctx } = _mkCanvas(W, H);
    const pageLabel = pages.length > 1 ? `PAGE ${pi + 1} / ${pages.length}` : "";
    _drawHeader(ctx, W, headerH, badge, dstr, logo, pageLabel);
    pageCards.forEach((card, ci) => {
      const x = padX + ci * (colW + colGap);
      const color = card.color;
      ctx.strokeStyle = "#0A0A0A"; ctx.lineWidth = 3;
      ctx.strokeRect(x, colTop, colW, colBodyH);
      ctx.fillStyle = color; ctx.fillRect(x, colTop, colW, colHeaderH);
      ctx.fillStyle = "#ffffff"; ctx.font = `900 22px ${F}`; ctx.textAlign = "left";
      ctx.fillText(card.label, x + 14, colTop + 31);
      const rows = (card.rows || []).slice(0, 6);
      const hideValue = false;  // show points for all games incl. Sudoku
      if (rows.length === 0) {
        ctx.fillStyle = "#9ca3af"; ctx.font = `500 16px ${F}`;
        ctx.fillText("No winners yet", x + 14, colTop + colHeaderH + 40);
      }
      rows.forEach((r, i) => {
        const ry = colTop + colHeaderH + 8 + i * rowH;
        const medal = i === 0 ? "#FFD600" : i === 1 ? "#e5e5e5" : i === 2 ? "#f5c98a" : "#f3f4f6";
        ctx.fillStyle = medal; ctx.fillRect(x + 12, ry + 8, 34, 34);
        ctx.strokeStyle = "#0A0A0A"; ctx.lineWidth = 2; ctx.strokeRect(x + 12, ry + 8, 34, 34);
        ctx.fillStyle = "#0A0A0A"; ctx.font = `900 20px ${F}`; ctx.textAlign = "center";
        ctx.fillText(String(r.rank), x + 29, ry + 33); ctx.textAlign = "left";
        ctx.fillStyle = "#0A0A0A"; ctx.font = `700 18px ${F}`;
        let name = r.name || "";
        const maxNameW = colW - 150;
        if (ctx.measureText(name).width > maxNameW) {
          while (name.length > 3 && ctx.measureText(name + "…").width > maxNameW) name = name.slice(0, -1);
          name += "…";
        }
        ctx.fillText(name, x + 56, ry + 26);
        ctx.fillStyle = "#737373"; ctx.font = `500 12px ${F}`;
        const sub = [r.employee_id, r.department, r.location].filter(Boolean).join(" · ") || "";
        let subT = sub; const subMax = colW - 150;
        if (ctx.measureText(subT).width > subMax) { while (subT.length > 3 && ctx.measureText(subT + "…").width > subMax) subT = subT.slice(0, -1); subT += "…"; }
        ctx.fillText(subT, x + 56, ry + 43);
        if (!hideValue) {
          ctx.fillStyle = color; ctx.font = `900 20px ${F}`; ctx.textAlign = "right";
          ctx.fillText(String(r.points), x + colW - 16, ry + 28);
          ctx.fillStyle = "#737373"; ctx.font = `600 11px ${F}`;
          ctx.fillText(card.unit || "PTS", x + colW - 16, ry + 43); ctx.textAlign = "left";
        }
        if (i < rows.length - 1) {
          ctx.strokeStyle = "#e5e5e5"; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.moveTo(x + 12, ry + rowH); ctx.lineTo(x + colW - 12, ry + rowH); ctx.stroke();
        }
      });
    });
    ctx.fillStyle = "#9ca3af"; ctx.font = `500 13px ${F}`; ctx.textAlign = "left";
    ctx.fillText("Generated " + new Date().toLocaleString(), padX + 4, H - 18);
    const suffix = pages.length > 1 ? `_page${pi + 1}` : "";
    out.push({ filename: `${fileBase}${suffix}.png`, dataUrl: canvas.toDataURL("image/png") });
  });
  return out;
}

function _roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Overlay the report date onto the template's calendar icon (top-right).
function _drawCalendarDate(ctx, dateStr) {
  let d = new Date((dateStr || "") + "T00:00:00");
  if (isNaN(d.getTime())) d = new Date();
  const weekday = d.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase();
  const month = d.toLocaleDateString("en-US", { month: "long" }).toUpperCase();
  const day = d.getDate();
  const year = String(d.getFullYear());
  const cx = 1560, maxW = 152;
  // Cover the baked-in text on the white calendar body.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(1480, 122, 160, 130);
  ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#0A2540"; _fitFont(ctx, weekday, 800, 21, maxW, 11);
  ctx.fillText(weekday, cx, 154);
  ctx.fillStyle = "#E11010"; _fitFont(ctx, `${month} ${day}`, 800, 23, maxW, 12);
  ctx.fillText(`${month} ${day}`, cx, 197);
  ctx.fillStyle = "#0A2540"; _fitFont(ctx, year, 800, 25, maxW, 14);
  ctx.fillText(year, cx, 239);
  ctx.textAlign = "left";
}

// Winners (per game, up to 6) rendered into a single box (left panel).
function _combinedWinnersBox(ctx, data, box) {
  const games = data.games || {}, units = data.units || {};
  const order = ["rebus", "word", "sudoku"];
  const p = 14, gap = 10;
  const colW = (box.w - p * 2 - gap * 2) / 3;
  const innerY = box.y + p, innerH = box.h - p * 2, thH = 24;
  order.forEach((key, ci) => {
    const meta = GAME_META[key] || { label: key.toUpperCase(), color: "#0A0A0A" };
    const x = box.x + p + ci * (colW + gap);
    const unit = (units[key] || "pts").toUpperCase();
    ctx.fillStyle = meta.color; _roundRect(ctx, x, innerY, colW, thH, 5); ctx.fill();
    ctx.fillStyle = "#fff"; ctx.font = `900 13px ${F}`; ctx.textAlign = "left";
    ctx.fillText(meta.label, x + 8, innerY + 16);
    const rows = (games[key] || []).slice(0, 6);
    const rowsTop = innerY + thH + 4, rowH = (innerH - thH - 4) / 6;
    if (rows.length === 0) {
      ctx.fillStyle = "#9ca3af"; ctx.font = `500 12px ${F}`; ctx.textAlign = "center";
      ctx.fillText("No winners yet", x + colW / 2, rowsTop + 26); ctx.textAlign = "left";
      return;
    }
    rows.forEach((r, i) => {
      const ry = rowsTop + i * rowH;
      const medal = i === 0 ? "#FFD600" : i === 1 ? "#e5e5e5" : i === 2 ? "#f5c98a" : "#f3f4f6";
      const bs = 22;
      ctx.fillStyle = medal; _roundRect(ctx, x + 2, ry + (rowH - bs) / 2, bs, bs, 4); ctx.fill();
      ctx.strokeStyle = "#0A0A0A"; ctx.lineWidth = 1.2; ctx.stroke();
      ctx.fillStyle = "#0A0A0A"; ctx.font = `900 13px ${F}`; ctx.textAlign = "center";
      ctx.fillText(String(r.rank), x + 2 + bs / 2, ry + rowH / 2 + 4);
      const nameX = x + bs + 8, ptsW = 44, nameMax = colW - bs - 10 - ptsW;
      ctx.textAlign = "left"; ctx.fillStyle = "#0A0A0A"; ctx.font = `700 13px ${F}`;
      let name = r.name || "";
      if (ctx.measureText(name).width > nameMax) { while (name.length > 2 && ctx.measureText(name + "…").width > nameMax) name = name.slice(0, -1); name += "…"; }
      ctx.fillText(name, nameX, ry + rowH / 2 - 1);
      ctx.fillStyle = "#737373"; ctx.font = `500 9px ${F}`;
      let sub = [r.employee_id, r.department].filter(Boolean).join(" · ") || "";
      if (ctx.measureText(sub).width > nameMax) { while (sub.length > 2 && ctx.measureText(sub + "…").width > nameMax) sub = sub.slice(0, -1); sub += "…"; }
      ctx.fillText(sub, nameX, ry + rowH / 2 + 11);
      ctx.textAlign = "right"; ctx.fillStyle = meta.color; ctx.font = `900 15px ${F}`;
      ctx.fillText(String(r.points), x + colW - 4, ry + rowH / 2 + 2);
      ctx.fillStyle = "#737373"; ctx.font = `600 8px ${F}`;
      ctx.fillText(unit, x + colW - 4, ry + rowH / 2 + 12);
      ctx.textAlign = "left";
      if (i < rows.length - 1) { ctx.strokeStyle = "#eee"; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x, ry + rowH); ctx.lineTo(x + colW, ry + rowH); ctx.stroke(); }
    });
  });
}

// Daily answers (Rebus / Word / Sudoku) rendered into a single box (right panel).
function _combinedAnswersBox(ctx, data, assets, box) {
  const cards = [];
  if ((data.rebus || []).length) cards.push({ type: "rebus", ...GAME_META.rebus, rebus: data.rebus, _img: (assets.rebusImgs || [])[0] });
  if (data.word) cards.push({ type: "word", ...GAME_META.word, word: data.word });
  if (data.sudoku) cards.push({ type: "sudoku", ...GAME_META.sudoku, sudoku: data.sudoku });
  const n = Math.max(1, cards.length), p = 14, gap = 10;
  const colW = (box.w - p * 2 - gap * (n - 1)) / n;
  const innerY = box.y + p, innerH = box.h - p * 2, thH = 24;
  cards.forEach((card, ci) => {
    const x = box.x + p + ci * (colW + gap), cx = x + colW / 2;
    ctx.fillStyle = card.color; _roundRect(ctx, x, innerY, colW, thH, 5); ctx.fill();
    ctx.fillStyle = "#fff"; ctx.font = `900 13px ${F}`; ctx.textAlign = "left";
    ctx.fillText(card.label, x + 8, innerY + 16);
    const bodyTop = innerY + thH + 8, bodyH = innerH - thH - 8;
    if (card.type === "sudoku") {
      const sud = card.sudoku;
      const G = Math.min(colW - 8, bodyH - 22);
      const gx = x + (colW - G) / 2, gy = bodyTop;
      _drawSudokuGrid(ctx, sud, gx, gy, G, true);
      ctx.fillStyle = "#0A0A0A"; ctx.font = `800 11px ${F}`; ctx.textAlign = "center";
      ctx.fillText(`Difficulty: ${(sud.difficulty || "medium").toUpperCase()}`, cx, gy + G + 16);
    } else if (card.type === "word") {
      const word = card.word; let ty = bodyTop + 8;
      ctx.textAlign = "center";
      ctx.fillStyle = "#737373"; ctx.font = `700 10px ${F}`; ctx.fillText("ANSWER", cx, ty); ty += 10;
      const aw = (word.word || "").toUpperCase();
      const af = _fitFont(ctx, aw, 900, 26, colW - 14, 14);
      ctx.fillStyle = "#1E88E5"; ctx.fillText(aw, cx, ty + af * 0.85); ty += af + 14;
      if (word.meaning) {
        ctx.fillStyle = "#737373"; ctx.font = `700 10px ${F}`; ctx.fillText("MEANING", cx, ty); ty += 16;
        ctx.fillStyle = "#0A0A0A"; ctx.font = `500 12px ${F}`; ctx.textAlign = "center";
        _wrapText(ctx, word.meaning, cx, ty, colW - 18, 15);
      }
    } else {
      const img = card._img; let ty = bodyTop;
      if (img && img.width) {
        const maxH = Math.min(bodyH - 56, 158);
        const r = Math.min((colW - 14) / img.width, maxH / img.height);
        const dw = img.width * r, dh = img.height * r;
        ctx.drawImage(img, cx - dw / 2, ty, dw, dh); ty += dh + 12;
      }
      const rb = (card.rebus || [])[0] || {};
      ctx.textAlign = "center";
      ctx.fillStyle = "#737373"; ctx.font = `700 10px ${F}`; ctx.fillText("ANSWER", cx, ty); ty += 10;
      const aw = (rb.answer || "").toUpperCase();
      const af = _fitFont(ctx, aw, 900, 24, colW - 14, 12);
      ctx.fillStyle = "#FF3B30"; ctx.fillText(aw, cx, ty + af * 0.85);
    }
    ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  });
}

// Single combined 1920x1080 image: template bg + calendar date + winners(left) + answers(right).
export function generateCombinedImage(winData, ansData, assets = {}) {
  const { canvas, ctx } = _mkCanvas(OUT_W, OUT_H);
  if (assets.template) ctx.drawImage(assets.template, 0, 0, OUT_W, OUT_H);
  const dateStr = ansData.date || winData.date;
  _drawCalendarDate(ctx, dateStr);
  _combinedWinnersBox(ctx, winData, { x: 14, y: 438, w: 984, h: 360 });
  _combinedAnswersBox(ctx, ansData, assets, { x: 1018, y: 438, w: 868, h: 360 });
  return [{ filename: `funzone_report_${dateStr}.png`, dataUrl: canvas.toDataURL("image/png") }];
}

export async function buildCombined(winData, ansData) {
  const template = await loadImg(window.location.origin + "/brand/report-template.png");
  const rebusImgs = await Promise.all((ansData.rebus || []).slice(0, 3).map((pz) => loadImg(pz.image_data)));
  return generateCombinedImage(winData, ansData, { template, rebusImgs });
}

export function downloadImages(images) {
  images.forEach(({ filename, dataUrl }) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  });
}

const LOGO_SRC = () => window.location.origin + "/brand/tekwissen-logo.png";

// Convenience: fetch-free builders that preload assets from already-fetched data.
export async function buildQuestionsOrAnswers(mode, ansData) {
  const logo = await loadImg(LOGO_SRC());
  const rebusImgs = await Promise.all((ansData.rebus || []).slice(0, 3).map((p) => loadImg(p.image_data)));
  const extraImgs = await Promise.all((ansData.extra || []).map((g) => loadImg(g.image_data)));
  return generateReportImages(mode, ansData, { logo, rebusImgs, extraImgs });
}

export async function buildWinners(winData) {
  const logo = await loadImg(LOGO_SRC());
  return generateWinnerImages(winData, logo);
}
